import React, { useState, useEffect, useCallback } from 'react';

const NutritionalInfoCard = ({ question, allNutritionalInfo, onAnswer }) => {
  const [questionType, setQuestionType] = useState('');
  const [options, setOptions] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [correctAnswer, setCorrectAnswer] = useState('');
  const [isAnswered, setIsAnswered] = useState(false);

  // Shuffle array function
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Generate distractors based on question type
  const generateDistractors = useCallback((correctInfo, allInfo, type) => {
    const distractors = allInfo.filter(info => info.dish !== correctInfo.dish);
    const shuffledDistractors = shuffleArray(distractors);
    return shuffledDistractors.slice(0, 3);
  }, []);

  // Setup question when component mounts or question changes
  useEffect(() => {
    if (!question) return;

    // Reset state
    setSelectedAnswer(null);
    setIsAnswered(false);

    // Randomly choose question type
    const questionTypes = ['allergens', 'dietary', 'modifications', 'calories'];
    const randomType = questionTypes[Math.floor(Math.random() * questionTypes.length)];
    setQuestionType(randomType);

    let correct, distractorOptions;
    const distractors = generateDistractors(question, allNutritionalInfo, randomType);

    switch (randomType) {
      case 'allergens':
        correct = question.allergens.join(', ') || 'None';
        distractorOptions = distractors.map(d => d.allergens.join(', ') || 'None');
        break;
      case 'dietary':
        const dietaryOptions = Object.entries(question.dietaryRestrictions)
          .filter(([key, value]) => value)
          .map(([key]) => key.replace(/([A-Z])/g, ' $1').toLowerCase());
        correct = dietaryOptions.length > 0 ? dietaryOptions.join(', ') : 'None suitable';
        distractorOptions = distractors.map(d => {
          const opts = Object.entries(d.dietaryRestrictions)
            .filter(([key, value]) => value)
            .map(([key]) => key.replace(/([A-Z])/g, ' $1').toLowerCase());
          return opts.length > 0 ? opts.join(', ') : 'None suitable';
        });
        break;
      case 'modifications':
        const modKeys = Object.keys(question.modifications);
        if (modKeys.length > 0) {
          const randomMod = modKeys[Math.floor(Math.random() * modKeys.length)];
          correct = question.modifications[randomMod];
          distractorOptions = distractors.map(d => {
            const dModKeys = Object.keys(d.modifications);
            if (dModKeys.length > 0) {
              return d.modifications[dModKeys[0]];
            }
            return 'No modifications available';
          });
        } else {
          correct = 'No modifications available';
          distractorOptions = ['Ask chef for options', 'Substitute ingredients', 'Serve as is'];
        }
        break;
      case 'calories':
        correct = `${question.calories} calories`;
        distractorOptions = distractors.map(d => `${d.calories} calories`);
        break;
      default:
        correct = 'Unknown';
        distractorOptions = ['Option 1', 'Option 2', 'Option 3'];
    }

    setCorrectAnswer(correct);
    const allOptions = [correct, ...distractorOptions];
    setOptions(shuffleArray(allOptions));
  }, [question, allNutritionalInfo, generateDistractors]);

  // Handle answer selection
  const handleAnswerClick = (answer) => {
    if (isAnswered) return;

    setSelectedAnswer(answer);
    setIsAnswered(true);
    
    const isCorrect = answer === correctAnswer;
    onAnswer(isCorrect, answer, correctAnswer);
  };

  // Get button class based on state
  const getButtonClass = (option) => {
    if (!isAnswered) return 'answer-button';
    
    let classes = 'answer-button disabled';
    
    if (option === correctAnswer) {
      classes += ' correct';
    } else if (option === selectedAnswer && option !== correctAnswer) {
      classes += ' incorrect';
    }
    
    return classes;
  };

  // Get question text based on type
  const getQuestionText = () => {
    switch (questionType) {
      case 'allergens':
        return 'What allergens does this dish contain?';
      case 'dietary':
        return 'Which dietary restrictions is this dish suitable for?';
      case 'modifications':
        return 'How can this dish be modified for dietary restrictions?';
      case 'calories':
        return 'How many calories does this dish contain?';
      default:
        return 'What nutritional information applies to this dish?';
    }
  };

  // Generate Google search URL
  const getSearchUrl = () => {
    return `https://google.com/search?q=${encodeURIComponent(`${question.dish} nutrition allergens dietary`)}`;
  };

  if (!question) return null;

  return (
    <div className="question-card">
      <div className="question">
        <strong>{getQuestionText()}</strong>
        <br /><br />
        <div className="nutritional-dish">
          "{question.dish}"
        </div>
        <br />
        {isAnswered && (
          <div className="nutritional-details">
            <div className="nutrition-facts">
              <strong>Nutritional Information:</strong>
              <p>Calories: {question.calories} | Protein: {question.protein} | Carbs: {question.carbs} | Fat: {question.fat}</p>
            </div>
            
            <div className="allergen-info">
              <strong>Allergens:</strong>
              <p>{question.allergens.length > 0 ? question.allergens.join(', ') : 'None'}</p>
            </div>
            
            {question.healthBenefits && question.healthBenefits.length > 0 && (
              <div className="health-benefits">
                <strong>Health Benefits:</strong>
                <ul>
                  {question.healthBenefits.map((benefit, index) => (
                    <li key={index}>{benefit}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {question.nutritionalHighlights && (
              <div className="nutritional-highlights">
                <strong>Key Point:</strong>
                <p><em>{question.nutritionalHighlights}</em></p>
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="answers">
        {options.map((option, index) => (
          <button
            key={index}
            className={getButtonClass(option)}
            onClick={() => handleAnswerClick(option)}
            disabled={isAnswered}
          >
            {option}
          </button>
        ))}
      </div>
      
      <a 
        href={getSearchUrl()} 
        target="_blank" 
        rel="noopener noreferrer"
        className="lookup-link"
      >
        🔍 Look Up Nutritional Info
      </a>
    </div>
  );
};

export default NutritionalInfoCard; 